const DEFAULT_ENDING_SOON_THRESHOLD_UNITS = 2;
const DEFAULT_PENDING_REMINDER_DAYS_BEFORE_DUE = 2;

function packageSchoolDate(now = new Date()) {
  return new Intl.DateTimeFormat('en-CA', { timeZone: 'Europe/Warsaw', year: 'numeric', month: '2-digit', day: '2-digit' }).format(new Date(now));
}

function effectivePackageStatus(enrollment, today = packageSchoolDate()) {
  return enrollment.status === 'active' && enrollment.validityEndDate && enrollment.validityEndDate < today
    ? 'expired' : enrollment.status;
}

const packageTerms = enrollment => enrollment.termsSnapshot || enrollment.product || {};

const isPackageEnrollment = enrollment => (
  packageTerms(enrollment).chargingStrategy === "consume_package_units" ||
  Boolean(enrollment.packageBalance) ||
  ["awaitingPayment", "active", "expired", "depleted", "cancelled"].includes(enrollment.status)
);

const daysBetween = (fromDate, toDate) => {
  const from = new Date(`${fromDate}T00:00:00.000Z`);
  const to = new Date(`${toDate}T00:00:00.000Z`);
  return Math.floor((to.getTime() - from.getTime()) / 86400000);
};

function packageCard(enrollment, { endingSoonThresholdUnits, today }) {
  const terms = packageTerms(enrollment);
  const balance = enrollment.packageBalance || null;
  const warnings = [];
  const status = effectivePackageStatus(enrollment, today);

  if (status === "awaitingPayment") {
    warnings.push("awaitingPayment");
  }
  if (
    status === "active" &&
    balance &&
    Number(balance.availableUnits || 0) <= endingSoonThresholdUnits
  ) {
    warnings.push("endingSoon");
  }

  return {
    id: enrollment.id,
    status,
    name: terms.name || enrollment.name || enrollment.id,
    classTypeId: terms.classTypeId || null,
    paymentDueDate: enrollment.paymentDueDate || null,
    validityEndDate: enrollment.validityEndDate || null,
    balance,
    warnings,
  };
}

function buildClientPackageProjection(enrollments = [], {
  clientId,
  today = packageSchoolDate(),
  endingSoonThresholdUnits = DEFAULT_ENDING_SOON_THRESHOLD_UNITS,
} = {}) {
  return (enrollments || [])
    .filter(enrollment => (!clientId || enrollment.clientId === clientId))
    .filter(isPackageEnrollment)
    .filter(enrollment => enrollment.status !== "cancelled")
    .map(enrollment => packageCard(enrollment, { endingSoonThresholdUnits, today }));
}

function buildOperatorPackageWorkLists(enrollments = [], {
  today = packageSchoolDate(),
  endingSoonThresholdUnits = DEFAULT_ENDING_SOON_THRESHOLD_UNITS,
  pendingReminderDaysBeforeDue = DEFAULT_PENDING_REMINDER_DAYS_BEFORE_DUE,
} = {}) {
  const packageEnrollments = (enrollments || []).filter(isPackageEnrollment);
  const endingSoon = packageEnrollments.filter((enrollment) => {
    const balance = enrollment.packageBalance || {};
    return effectivePackageStatus(enrollment, today) === "active" &&
      Number(balance.availableUnits || 0) <= endingSoonThresholdUnits;
  });
  const pendingAutoCancelSoon = packageEnrollments.filter((enrollment) => {
    if (enrollment.status !== "awaitingPayment" || !today || !enrollment.paymentDueDate) return false;
    const daysUntilDue = daysBetween(today, enrollment.paymentDueDate);
    return daysUntilDue >= 0 && daysUntilDue <= pendingReminderDaysBeforeDue;
  });
  const expiredWithUnusedUnits = packageEnrollments.filter((enrollment) => {
    const balance = enrollment.packageBalance || {};
    return effectivePackageStatus(enrollment, today) === "expired" && Number(balance.availableUnits || 0) > 0;
  });

  return {
    endingSoon: endingSoon.map(enrollment => packageCard(enrollment, { endingSoonThresholdUnits, today })),
    pendingAutoCancelSoon: pendingAutoCancelSoon.map(enrollment => packageCard(enrollment, { endingSoonThresholdUnits, today })),
    expiredWithUnusedUnits: expiredWithUnusedUnits.map(enrollment => packageCard(enrollment, { endingSoonThresholdUnits, today })),
  };
}

module.exports = {
  packageSchoolDate,
  effectivePackageStatus,
  buildClientPackageProjection,
  buildOperatorPackageWorkLists,
};
